'use client';

import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLocale } from 'next-intl';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Breadcrumb from '@/components/Breadcrumb';
import StructuredData from '@/components/StructuredData';
import { products, getProductCopy } from '@/data/products';

const content = {
  en: {
    breadcrumbHome: 'Home',
    breadcrumbCurrent: 'Products',
    badge: 'Our Products',
    title: 'Products we built ourselves',
    subtitle:
      'Most of what we do is built for clients. These are the products we own outright — our idea, our engineering, live in production. They are also the clearest proof of what we can build for you.',
    whatItDoes: 'What it does',
    visit: 'Visit',
    liveNow: 'Live now',
    ctaTitle: 'Want something like this for your business?',
    ctaSubtitle:
      'We build custom AI agents, automations and websites for companies across Lithuania and beyond. Tell us what you have in mind.',
    ctaButton: 'Get in touch',
  },
  lt: {
    breadcrumbHome: 'Pagrindinis',
    breadcrumbCurrent: 'Produktai',
    badge: 'Mūsų Produktai',
    title: 'Produktai, kuriuos sukūrėme patys',
    subtitle:
      'Daugumą darbų kuriame klientams. Šie produktai — visiškai mūsų: mūsų idėja, mūsų inžinerija, jau veikianti realiai. Kartu tai ir aiškiausias įrodymas, ką galime sukurti jums.',
    whatItDoes: 'Ką ji daro',
    visit: 'Aplankyti',
    liveNow: 'Jau veikia',
    ctaTitle: 'Norite tokio sprendimo savo verslui?',
    ctaSubtitle:
      'Kuriame individualius AI agentus, automatizacijas ir svetaines įmonėms Lietuvoje ir užsienyje. Parašykite, ką turite omenyje.',
    ctaButton: 'Susisiekti',
  },
};

export default function ProductsPage() {
  const locale = useLocale();
  const isLT = locale === 'lt';
  const c = content[locale as keyof typeof content] || content.en;
  const prefix = isLT ? '/lt' : '';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <StructuredData type="organization" />
      <Header />
      <Breadcrumb
        items={[{ name: c.breadcrumbHome, url: prefix || '/' }]}
        currentPage={c.breadcrumbCurrent}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-20 sm:py-28">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-violet-500/25 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:72px_72px]" />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block rounded-full bg-violet-500/15 border border-violet-400/25 px-4 py-2 text-sm font-medium text-violet-200 mb-6">
              {c.badge}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {c.title}
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              {c.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 sm:py-24 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {products.map((product, index) => {
            const copy = getProductCopy(product, locale);

            return (
              <motion.article
                key={product.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="overflow-hidden rounded-3xl bg-white border border-gray-100 shadow-xl"
              >
                {/* Product header. Deliberately darker than `product.accent`: the
                    kalba.ai wordmark's sage accent only reaches 2.3:1 on violet-600,
                    below the 3:1 WCAG large-text floor. On violet-900/indigo-950 it
                    clears 4.4:1. The lighter accent gradient stays on the buttons,
                    where white text already passes comfortably. */}
                <div className="bg-gradient-to-br from-violet-900 to-indigo-950 px-8 py-12 sm:px-12">
                  <div className="flex flex-wrap items-center gap-4 mb-6">
                    {/* Wordmark is light-on-transparent and sits on the accent gradient. */}
                    {product.logo && product.logoSize ? (
                      <Image
                        src={product.logo}
                        alt={product.name}
                        width={product.logoSize.width}
                        height={product.logoSize.height}
                        className="h-11 sm:h-14 w-auto"
                        priority
                        // SVG is vector — see ProductsSection for why this bypasses
                        // the image optimizer.
                        unoptimized
                      />
                    ) : (
                      <span className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                        {product.name}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/25 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-300 animate-pulse" />
                      {c.liveNow}
                    </span>
                  </div>
                  <p className="text-xl sm:text-2xl font-semibold text-white/95 max-w-2xl leading-snug">
                    {copy.tagline}
                  </p>
                </div>

                {/* Product body */}
                <div className="px-8 py-10 sm:px-12 sm:py-12">
                  <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mb-10">
                    {copy.description}
                  </p>

                  {/* Highlights */}
                  <div className="flex flex-wrap gap-3 mb-12">
                    {copy.highlights.map((highlight) => (
                      <span
                        key={highlight}
                        className="inline-flex items-center gap-2 rounded-full bg-violet-50 border border-violet-100 px-4 py-2 text-sm font-medium text-violet-700"
                      >
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        {highlight}
                      </span>
                    ))}
                  </div>

                  {/* Capabilities */}
                  <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-8">
                    {c.whatItDoes}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-x-10 gap-y-10 mb-12">
                    {copy.features.map((feature) => (
                      <div key={feature.title} className="flex gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-xl" aria-hidden="true">
                          {feature.icon}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 mb-1.5">{feature.title}</h3>
                          <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <motion.a
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ y: -2 }}
                    whileTap={{ y: 0 }}
                    className={`inline-flex items-center gap-2.5 rounded-xl bg-gradient-to-r ${product.accent} px-7 py-4 font-semibold text-white shadow-lg shadow-violet-500/25 transition-shadow hover:shadow-xl hover:shadow-violet-500/40`}
                  >
                    {c.visit} {product.name}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </motion.a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {c.ctaTitle}
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            {c.ctaSubtitle}
          </p>
          <a href={`${prefix}/kontaktai`} className="btn-primary">
            {c.ctaButton}
          </a>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}
